import {
  Info,
  Target,
  Star,
  SquareCheck,
  FileText,
  Award,
  CircleQuestionMark,
  Calendar,
  Share2,
  BookOpen,
  TrendingUp,
  RefreshCw,
  Home,
  FolderOpen,
  GraduationCap,
  BarChart3,
} from "lucide-react";

import {
  CHECKLIST_LABEL_LINKS,
  GuidancePageCardItem,
  GuidancePageSection,
  PreparationChecklistAccordionItem,
} from "./types";
import {
  PRME_SIP_2026_WEBINAR,
  PRME_SIP_2026_UK_IRELAND_FIRST_TIME_REPORTERS,
  PRME_SIP_2026_UK_IRELAND_ENHANCE_QUALITY_AND_REPORTING,
} from "@/shared/utils/constants";

export const GUIDANCE_PAGE_ANCHOR_ITEMS: GuidancePageSection[] = [
  {
    key: "about",
    href: "#about",
    titleKey: "aboutTheSurvey",
    icon: Info,
  },
  {
    key: "tool-for-impact",
    href: "#tool-for-impact",
    titleKey: "toolForImpact",
    icon: BarChart3,
  },
  {
    key: "new",
    href: "#new-for-2026",
    titleKey: "newFor2026",
    icon: Star,
  },
  {
    key: "checklist",
    href: "#checklist",
    titleKey: "checklist",
    icon: SquareCheck,
  },
  {
    key: "exemplary-sips",
    href: "#exemplary-sips",
    titleKey: "exemplarySurveys",
    icon: Award,
  },
  {
    key: "how-to",
    href: "#how-to-complete",
    titleKey: "howToCompleteTheSurvey",
    icon: FileText,
  },
  {
    key: "faq",
    href: "#faq",
    titleKey: "faq",
    icon: CircleQuestionMark,
  },
  {
    key: "calendar",
    href: "#calendar",
    titleKey: "calendar",
    icon: Calendar,
  },
];

export const CALENDAR_ITEMS: {
  key: string;
  dateKey: string;
  titleKey: string;
  descriptionKey: string;
  tagKey?: string;
  linkUrl?: string;
  linkLabelKey?: string;
}[] = [
  {
    key: "reporting-opens",
    dateKey: "calendarReportingOpensDate",
    titleKey: "calendarReportingOpensTitle",
    descriptionKey: "calendarReportingOpensDescription",
  },
  {
    key: "standard-deadline",
    dateKey: "calendarStandardDeadlineDate",
    titleKey: "calendarStandardDeadlineTitle",
    descriptionKey: "calendarStandardDeadlineDescription",
  },
  {
    key: "late-deadline",
    dateKey: "calendarLateDeadlineDate",
    titleKey: "calendarLateDeadlineTitle",
    descriptionKey: "calendarLateDeadlineDescription",
    tagKey: "calendarLateDeadlineTag",
  },
];

export const CALENDAR_EVENTS_ITEMS: {
  key: string;
  dateKey: string;
  timeKey: string;
  titleKey: string;
  descriptionKey: string;
  isCompleted?: boolean;
  linkUrl?: string;
  linkLabelKey?: string;
}[] = [
  {
    key: "informational-webinar",
    dateKey: "calendarInformationalWebinarDate",
    timeKey: "calendarInformationalWebinarTime",
    titleKey: "calendarInformationalWebinarTitle",
    descriptionKey: "calendarInformationalWebinarDescription",
    isCompleted: true,
    linkUrl: PRME_SIP_2026_WEBINAR,
    linkLabelKey: "calendarInformationalWebinarButton",
  },
  {
    key: "first-time-reporters",
    dateKey: "calendarFirstTimeReportersDate",
    timeKey: "calendarFirstTimeReportersTime",
    titleKey: "calendarFirstTimeReportersTitle",
    descriptionKey: "calendarFirstTimeReportersDescription",
    linkUrl: PRME_SIP_2026_UK_IRELAND_FIRST_TIME_REPORTERS,
  },
  {
    key: "enhance-quality-and-reporting",
    dateKey: "calendarEnhanceQualityAndReportingDate",
    timeKey: "calendarEnhanceQualityAndReportingTime",
    titleKey: "calendarEnhanceQualityAndReportingTitle",
    descriptionKey: "calendarEnhanceQualityAndReportingDescription",
    linkUrl: PRME_SIP_2026_UK_IRELAND_ENHANCE_QUALITY_AND_REPORTING,
  },
];

export const FAQ_ITEMS: {
  key: string;
  questionKey: string;
  answerKey: string;
}[] = [
  {
    key: "what-is-sip",
    questionKey: "faq1Question",
    answerKey: "faq1Answer",
  },
  {
    key: "how-often",
    questionKey: "faq2Question",
    answerKey: "faq2Answer",
  },
  {
    key: "edit-after",
    questionKey: "faq3Question",
    answerKey: "faq3Answer",
  },
  {
    key: "miss-deadline",
    questionKey: "faq4Question",
    answerKey: "faq4Answer",
  },
  {
    key: "find-examples",
    questionKey: "faq5Question",
    answerKey: "faq5Answer",
  },
  {
    key: "who-involved",
    questionKey: "faq6Question",
    answerKey: "faq6Answer",
  },
  {
    key: "sip-report-period",
    questionKey: "faq7Question",
    answerKey: "faq7Answer",
  },
  {
    key: "preview-before-opens",
    questionKey: "faq8Question",
    answerKey: "faq8Answer",
  },
  {
    key: "word-or-excel-version",
    questionKey: "faq9Question",
    answerKey: "faq9Answer",
  },
  {
    key: "time-commitment",
    questionKey: "faq10Question",
    answerKey: "faq10Answer",
  },
  {
    key: "ranking-accreditation",
    questionKey: "faq11Question",
    answerKey: "faq11Answer",
  },
  {
    key: "commons-login",
    questionKey: "faq12Question",
    answerKey: "faq12Answer",
  },
  {
    key: "invite-members-commons",
    questionKey: "faq13Question",
    answerKey: "faq13Answer",
  },
  {
    key: "commons-permissions",
    questionKey: "faq14Question",
    answerKey: "faq14Answer",
  },
  {
    key: "save-as-you-go",
    questionKey: "faq15Question",
    answerKey: "faq15Answer",
  },
  {
    key: "who-views-sip-data",
    questionKey: "faq16Question",
    answerKey: "faq16Answer",
  },
  {
    key: "why-revamp-sip",
    questionKey: "faq17Question",
    answerKey: "faq17Answer",
  },
  {
    key: "process-revamping-sip",
    questionKey: "faq18Question",
    answerKey: "faq18Answer",
  },
  {
    key: "who-involved-updating",
    questionKey: "faq19Question",
    answerKey: "faq19Answer",
  },
];

export const ABOUT_THE_SIP_CARDS: GuidancePageCardItem[] = [
  {
    key: "share",
    icon: Share2,
    titleKey: "aboutCardShareTitle",
    descriptionKey: "aboutCardShareDescription",
  },
  {
    key: "inform",
    icon: BookOpen,
    titleKey: "aboutCardInformTitle",
    descriptionKey: "aboutCardInformDescription",
  },
  {
    key: "progress",
    icon: TrendingUp,
    titleKey: "aboutCardProgressTitle",
    descriptionKey: "aboutCardProgressDescription",
  },
];

export const NEW_FOR_2026_CARDS: GuidancePageCardItem[] = [
  {
    key: "simplified",
    icon: FileText,
    titleKey: "newFor2026CardSimplifiedTitle",
    descriptionKey: "newFor2026CardSimplifiedDescription",
  },
  {
    key: "goal",
    icon: Target,
    titleKey: "newFor2026CardGoalTitle",
    descriptionKey: "newFor2026CardGoalDescription",
  },
  {
    key: "reuse",
    icon: RefreshCw,
    titleKey: "newFor2026CardReuseTitle",
    descriptionKey: "newFor2026CardReuseDescription",
  },
  {
    key: "publication",
    icon: BookOpen,
    titleKey: "newFor2026CardPublicationTitle",
    descriptionKey: "newFor2026CardPublicationDescription",
  },
];

export const PREPARATION_CHECKLIST_ACCORDIONS: PreparationChecklistAccordionItem[] =
  [
    {
      key: "set-up",
      icon: Home,
      titleKey: "checklistSetUpTitle",
      items: [
        {
          id: "set-up-1",
          labelKey: "checklistSetUpItem1",
          descriptionKeys: ["checklistSetUpItem1Description"],
        },
        {
          id: "set-up-2",
          labelKey: "checklistSetUpItem2",
          descriptionKeys: ["checklistSetUpItem2Description"],
        },
        {
          id: "set-up-3",
          labelKey: "checklistSetUpItem3",
          descriptionKeys: [
            "checklistSetUpItem3Description1",
            "checklistSetUpItem3Description2",
            "checklistSetUpItem3Description3",
            "checklistSetUpItem3Description4",
          ],
        },
      ],
    },
    {
      key: "familiarize",
      icon: BookOpen,
      titleKey: "checklistFamiliarizeTitle",
      items: [
        {
          id: "familiarize-1",
          labelKey: "checklistFamiliarizeItem1",
          labelLink: CHECKLIST_LABEL_LINKS.PREVIEW,
        },
        {
          id: "familiarize-2",
          labelKey: "checklistFamiliarizeItem2",
          labelLink: CHECKLIST_LABEL_LINKS.TUTORIAL,
        },
        {
          id: "familiarize-3",
          labelKey: "checklistFamiliarizeItem3",
        },
      ],
    },
    {
      key: "organize",
      icon: FolderOpen,
      titleKey: "checklistOrganizeTitle",
      items: [
        {
          id: "organize-1",
          labelKey: "checklistOrganizeItem1",
        },
        {
          id: "organize-2",
          labelKey: "checklistOrganizeItem2",
        },
        {
          id: "organize-3",
          labelKey: "checklistOrganizeItem3",
        },
      ],
    },
    {
      key: "strategize",
      icon: Share2,
      titleKey: "checklistStrategizeTitle",
      items: [
        {
          id: "strategize-1",
          labelKey: "checklistStrategizeItem1",
        },
        {
          id: "strategize-2",
          labelKey: "checklistStrategizeItem2",
        },
        {
          id: "strategize-3",
          labelKey: "checklistStrategizeItem3",
        },
        {
          id: "strategize-4",
          labelKey: "checklistStrategizeItem4",
        },
      ],
    },
    {
      key: "equip",
      icon: GraduationCap,
      titleKey: "checklistEquipTitle",
      items: [
        { id: "equip-1", labelKey: "checklistEquipItem1" },
        { id: "equip-2", labelKey: "checklistEquipItem2" },
      ],
    },
  ];
